// «Informe del vehículo» en PDF (como el de Dumax Pro/Mapon, min 3-7 del video
// del 26-oct): encabezado, puntuación general, criterios, puntuación por semana
// y rendimiento. jsPDF + autotable, como el resto de exportaciones.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { brand } from "../../../config/theme";
import { gradeOf, type DrivingScore, type ParamKey } from "./drivingScore";

const rgb = (hex: string): [number, number, number] => {
  const n = Number.parseInt((hex || "#74fa4c").replace("#", "").slice(0, 6), 16);
  return Number.isNaN(n) ? [116, 250, 76] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Mismos colores que las letras de la pantalla (GRADE_CLASS de BehaviorSection).
const GRADE_RGB: Record<string, [number, number, number]> = {
  A: [22, 163, 74], B: [132, 204, 22], C: [250, 204, 21], D: [245, 158, 11], E: [249, 115, 22], F: [239, 68, 68], G: [153, 27, 27], "—": [209, 213, 219],
};

const CRITERIA: { key: ParamKey; label: string; unit: string; fmt: (v: number) => string }[] = [
  { key: "idle", label: "Ralentí excesivo", unit: "% del tiempo con motor encendido", fmt: (v) => `${v.toFixed(1)} %` },
  { key: "eco", label: "Velocidad eco", unit: "% del tiempo conducido", fmt: (v) => `${v.toFixed(1)} %` },
  { key: "harshBrake", label: "Frenado brusco", unit: "número de eventos", fmt: (v) => String(v) },
  { key: "harshAccel", label: "Aceleración brusca", unit: "número de eventos", fmt: (v) => String(v) },
  { key: "harshTurn", label: "Giro brusco", unit: "número de eventos", fmt: (v) => String(v) },
  { key: "speeding", label: "Exceso de velocidad", unit: "% del tiempo conducido", fmt: (v) => `${v.toFixed(1)} %` },
];

const hm = (ms: number) => {
  const min = Math.round(ms / 60000);
  return `${Math.floor(min / 60)} h ${min % 60} min`;
};

export interface VehicleReportInput {
  companyName: string;
  period: string;
  vehicleName: string;
  driverName: string | null;
  score: DrivingScore;
  // Barras de la puntuación histórica: mes a mes si el API ya la da (fase 2),
  // si no, por semana dentro del periodo.
  history: { title: string; bars: { label: string; overall: number | null }[] };
  fileName: string;
}

export const buildVehicleReportPdf = (r: VehicleReportInput): jsPDF => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const brandRgb = rgb(brand.primary);

  doc.setFillColor(...brandRgb);
  doc.rect(14, 14, 2.5, 12, "F");
  doc.setTextColor(17, 24, 39);
  doc.setFontSize(20);
  doc.text("Informe del vehículo", 20, 23);

  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  const rows: [string, string][] = [
    ["Compañía", r.companyName || "—"],
    ["Periodo", r.period],
    ["Vehículo", r.vehicleName],
    ["Conductor", r.driverName || "Sin conductor asignado"],
  ];
  rows.forEach(([k, v], i) => {
    doc.setTextColor(107, 114, 128);
    doc.text(k, 14, 36 + i * 6);
    doc.setTextColor(17, 24, 39);
    doc.text(v, 40, 36 + i * 6);
  });

  // Puntuación general
  const g = gradeOf(r.score.overall);
  doc.setFillColor(...GRADE_RGB[g]);
  doc.roundedRect(W - 50, 30, 36, 24, 2, 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text(g, W - 32, 44, { align: "center" });
  doc.setFontSize(9);
  doc.text(r.score.overall == null ? "Sin manejo" : `${r.score.overall.toFixed(1)} de 100`, W - 32, 50, { align: "center" });
  doc.setTextColor(107, 114, 128);
  doc.text("Puntuación general", W - 32, 59, { align: "center" });

  // Criterios
  autoTable(doc, {
    startY: 66,
    head: [["Comportamiento del vehículo", "", "Valor", "Calificación"]],
    body: CRITERIA.map((c) => {
      const p = r.score.params[c.key];
      return [c.label, c.unit, p.score == null ? "Sin dato" : c.fmt(p.value), p.score == null ? "—" : `${gradeOf(p.score)} · ${p.score.toFixed(0)}`];
    }),
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: brandRgb, textColor: [17, 24, 39] },
    columnStyles: { 1: { textColor: [107, 114, 128], fontSize: 8 }, 2: { halign: "right" }, 3: { halign: "center" } },
  });

  // Puntuación por semana (barras)
  let y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text(r.history.title, 14, y);
  y += 4;
  const chartH = 40;
  const n = Math.max(1, r.history.bars.length);
  const slot = Math.min(28, (W - 28) / n);
  r.history.bars.forEach((w, i) => {
    const x = 14 + i * slot;
    const h = w.overall == null ? 0 : (w.overall / 100) * chartH;
    const gw = gradeOf(w.overall);
    doc.setFillColor(...GRADE_RGB[gw]);
    if (h > 0) doc.rect(x + 3, y + chartH - h, slot - 6, h, "F");
    doc.setFontSize(8);
    doc.setTextColor(17, 24, 39);
    doc.text(w.overall == null ? "—" : `${gw} ${w.overall.toFixed(0)}`, x + slot / 2, y + chartH - h - 1.5, { align: "center" });
    doc.setTextColor(107, 114, 128);
    doc.text(w.label, x + slot / 2, y + chartH + 4, { align: "center" });
  });
  if (!r.history.bars.length) {
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    doc.text("Sin manejo en el periodo.", 14, y + 8);
  }
  y += chartH + 12;

  // Rendimiento
  const s = r.score;
  const harsh = s.params.harshBrake.value + s.params.harshAccel.value + s.params.harshTurn.value;
  autoTable(doc, {
    startY: y,
    head: [["Rendimiento del vehículo", ""]],
    body: [
      ["Distancia total", `${s.distanceKm.toFixed(1)} km`],
      ["Tiempo de conducción", hm(s.drivingMs)],
      ["Ralentí", hm(s.idleMs)],
      ["Motor encendido", hm(s.engineOnMs)],
      ["Velocidad media", s.avgSpeed == null ? "—" : `${s.avgSpeed.toFixed(1)} km/h`],
      ["Eventos bruscos (frenado, aceleración, giro)", String(harsh)],
    ],
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: brandRgb, textColor: [17, 24, 39] },
    columnStyles: { 1: { halign: "right" } },
  });

  doc.setFontSize(7);
  doc.setTextColor(156, 163, 175);
  doc.text(`Generado el ${new Date().toLocaleString("es-MX")}`, 14, doc.internal.pageSize.getHeight() - 8);
  return doc;
};

export const downloadVehicleReportPdf = (r: VehicleReportInput) => buildVehicleReportPdf(r).save(r.fileName);
